'use client';

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapPinOff } from 'lucide-react';
import { isPlottableCoord } from '@/lib/geocode';
import { supportsMapGL } from '@/lib/mapSupport';
import { staticMapForPoint } from '@/lib/staticMap';

/** A capability that cannot change during a page's life needs no subscription. */
const NEVER_CHANGES = () => () => {};
/** The server has no `window`; it ships the interactive container either way. */
const SERVER_ASSUMES_SUPPORTED = () => true;

interface ListingLocationMapProps {
  latitude: number;
  longitude: number;
  address: string;
}

/**
 * A small interactive map pinning the home. Renders nothing when the listing
 * has no honest location (missing / (0,0) null-island / out-of-range) — a home
 * that would otherwise plot in the Atlantic is cleanly absent instead. Mapbox
 * (~heavy) is lazy-loaded on mount, so detail pages without a rendered map pay
 * nothing for it; page scroll is never hijacked (scrollZoom off).
 */
export default function ListingLocationMap({
  latitude,
  longitude,
  address,
}: ListingLocationMapProps) {
  const plottable = isPlottableCoord(latitude, longitude);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  /**
   * Whether this browser can run the map — read as an external value, not as
   * initial state.
   *
   * This section is server-rendered and the server always answers "supported"
   * (there is no `window` to probe). A client that answered "unsupported" on its
   * *first* render therefore swapped the map container for an `<img>` mid
   * hydration: observed live on the deployed listing page as **React error
   * #418** on every device without WebGL 2. React recovers by re-rendering, so
   * the still map appeared anyway — but the error is real, it reaches Cloud
   * Logging through the client-error reporter, and a mismatch is licensed to
   * throw away the subtree's state.
   *
   * `useSyncExternalStore` is the sanctioned shape for exactly this: hydrate
   * with the server's answer, then adopt the browser's. The store never changes,
   * so `subscribe` is a no-op.
   *
   * See lib/mapSupport for the probe itself: the old inline version leaked a
   * WebGL context per mount until the browser's cap was reached, and accepted
   * WebGL 1, which Mapbox GL v3 cannot use.
   */
  const supported = useSyncExternalStore(NEVER_CHANGES, supportsMapGL, SERVER_ASSUMES_SUPPORTED);
  // Mapbox threw on construction (a supported-looking device that isn't).
  const [mapFailed, setMapFailed] = useState(false);
  const unavailable = !supported || mapFailed;
  // Only built when the live map can't run.
  const staticSrc = unavailable ? staticMapForPoint({ latitude, longitude }) : null;

  useEffect(() => {
    if (unavailable) return;
    // `supported` is still the server's optimistic answer on the very first
    // pass of effects, so ask the (cached) probe directly rather than pulling
    // 1.5 MB of Mapbox onto a device that cannot use it.
    if (!supportsMapGL()) return;
    if (!plottable || !containerRef.current || mapRef.current) return;
    let cancelled = false;

    (async () => {
      const mapboxgl = (await import('mapbox-gl')).default;
      if (cancelled || !containerRef.current || mapRef.current) return;
      if (process.env.NEXT_PUBLIC_MAPBOX_TOKEN) {
        mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
      }
      let map: MapboxMap;
      try {
        map = new mapboxgl.Map({
          container: containerRef.current,
          style: 'mapbox://styles/mapbox/light-v11',
          center: [longitude, latitude],
          zoom: 14,
          scrollZoom: false, // never hijack page scroll on an embedded map
          attributionControl: true,
        });
      } catch (err) {
        console.warn('location map unavailable:', err);
        if (!cancelled) setMapFailed(true);
        return;
      }
      mapRef.current = map;
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
      new mapboxgl.Marker({ color: '#a41f2e' }).setLngLat([longitude, latitude]).addTo(map);
    })();

    return () => {
      cancelled = true;
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, [plottable, unavailable, latitude, longitude]);

  // No honest location → the section is cleanly absent (never a fake pin).
  if (!plottable) return null;

  return (
    <section aria-labelledby="location-heading">
      <h2 id="location-heading" className="font-display mb-4 text-2xl text-ink">
        Location
      </h2>
      {unavailable ? (
        // A still map needs no WebGL. For a single home it carries essentially
        // all of the map's value — where is this? — so a device that can't run
        // Mapbox GL still gets an answer instead of an apology.
        staticSrc ? (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element -- external
                still-map URL, already sized; the optimizer adds cost, not value. */}
            <img
              src={staticSrc}
              alt={`Map showing the location of ${address}`}
              width={640}
              height={360}
              className="w-full rounded-card border border-hairline"
              loading="lazy"
            />
            <p className="mt-2 text-xs text-ink-faint">
              Still map — this device can&apos;t run the interactive map.
            </p>
          </>
        ) : (
          <div className="flex h-40 items-center justify-center rounded-card border border-hairline bg-surface">
            <div className="flex flex-col items-center gap-2 px-6 text-center">
              <MapPinOff className="h-6 w-6 text-ink-faint" aria-hidden />
              <p className="text-sm text-ink-muted">The map couldn&apos;t load on this device.</p>
            </div>
          </div>
        )
      ) : (
        <div
          ref={containerRef}
          className="h-72 w-full overflow-hidden rounded-card border border-hairline"
          aria-label={`Map showing the location of ${address}`}
          role="img"
        />
      )}
      <p className="mt-2 text-xs text-ink-faint">Approximate location based on the listing address.</p>
    </section>
  );
}
